import fp from "lodash/fp"

import type { TFunction } from "i18next"

import type {
  Conductor,
  ConductorLayer,
  ConductorResistanceSpec,
  ConductorStrandDimensions,
} from "types/conductors.types"
import type { SimpleTableColumn } from "widgets/common/SimpleTable"
import type { SpecField } from "widgets/conductors/ConductorSpecGroup"

import { customValueFormatter } from "helpers/formatters/plots/plots"
import { formatDayFromISO } from "helpers/utils/dateUtils"

/**
 * Every field the conductor spec places itself, whether as a group row, in the
 * title or in one of the tables. Anything the API sends that is not in here is
 * shown bare under "Other", and adding a field to Conductor stops compiling until
 * it is accounted for one way or the other.
 */
const PLACED_FIELDS: Record<keyof Conductor, true> = {
  absorptivity: true,
  bending_amplitude_limit: true,
  coef_thermal_expansion: true,
  created_at: true,
  diameter: true,
  emergency_temperature_max: true,
  emissivity: true,
  endurance_limit: true,
  greasing: true,
  heat_capacity: true,
  id: true,
  inner_diameter: true,
  layers: true,
  manufacturer: true,
  mass_per_unit_length: true,
  name: true,
  nominal_section: true,
  rated_tensile_strength: true,
  resistance_specs: true,
  standard: true,
  temperature_max: true,
  type: true,
  ultimate_tensile_strength: true,
  updated_at: true,
}

/**
 * The diameter-like measure of a strand, whichever its shape happens to record.
 * A trapezoid or z-shaped strand gives an equivalent round diameter rather than
 * a real one, and a tube gives its outer diameter. [m]
 */
export const getStrandDiameter = (
  dimensions: ConductorStrandDimensions | null | undefined,
): number | null => {
  if (!dimensions) return null
  if ("diameter" in dimensions) return dimensions.diameter
  if ("equivalent_round_diameter" in dimensions) {
    return dimensions.equivalent_round_diameter
  }
  if ("outer_diameter" in dimensions) return dimensions.outer_diameter
  return null
}

/** The model a conductor goes by, or its id when the catalog entry has no name. */
export const conductorName = (t: TFunction, conductor: Conductor): string =>
  conductor.name ?? t("conductors.CONDUCTOR_NUMBER", { id: conductor.id })

type ConductorEnumGroup =
  | "CURRENT_TYPES"
  | "GREASINGS"
  | "MATERIALS"
  | "SHAPES"
  | "STANDARDS"
  | "TYPES"

/** An API name we have no wording for, made readable: "lay_ratio" -> "Lay ratio". */
export const humanizeApiName = (name: string): string => {
  const spaced = name.replace(/_/g, " ")
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/**
 * A label for a value of one of the conductor enums. The models team owns those
 * enums and can add to them, and `standard` accepts a free string on top, so a
 * value we have no label for falls back to reading as itself rather than as a
 * missing translation key.
 */
export const conductorEnumLabel = (
  t: TFunction,
  group: ConductorEnumGroup,
  value: string | null | undefined,
): string | undefined => {
  if (!value) return undefined

  return t(`conductors.${group}.${value.toUpperCase()}`, {
    defaultValue: humanizeApiName(value),
  })
}

/**
 * A value of a field the API sent that this dialog knows nothing about, so there
 * is no unit to put on it. Units live only in the API's own model, never on the
 * wire, which is why an unknown number can only be shown bare.
 */
export const formatUnknownValue = (
  value: unknown,
  locale: string,
): string | undefined => {
  if (fp.isNil(value) || typeof value === "object") return undefined
  if (typeof value !== "number") return String(value)

  // Significant digits rather than decimal places: an unknown field could hold
  // any magnitude, and 0.0000286 must not round away to 0.
  return new Intl.NumberFormat(locale, { maximumSignificantDigits: 6 }).format(value)
}

/**
 * The API stores strict SI, which is not how conductor datasheets read. The
 * shared SI-prefix formatter cannot do the conversion: it prefixes the base
 * unit, so a section in m² would come out as µm² rather than mm².
 */
const scaled =
  (factor: number, suffix: string, decimalPlaces: number) =>
  (value: number, locale: string) =>
    customValueFormatter({ suffix, decimalPlaces, formatUnits: false, locale })(
      value * factor,
    )

/** [m] as mm. */
export const formatConductorLength = scaled(1e3, "mm", 2)

/** [m²] as mm². */
export const formatConductorSection = scaled(1e6, "mm²", 1)

/** [Ω/m] as Ω/km. */
export const formatConductorResistance = scaled(1e3, "Ω/km", 4)

/** [N] as kN. */
export const formatConductorForce = scaled(1e-3, "kN", 1)

/** [N/m²] as MPa. */
export const formatConductorStress = scaled(1e-6, "MPa", 1)

/** [kg/m]. */
export const formatConductorMass = scaled(1, "kg/m", 3)

/** [J/(m·°C)]. */
export const formatConductorHeatCapacity = scaled(1, "J/(m·°C)", 1)

/** [°C]. */
export const formatConductorTemperature = scaled(1, "°C", 1)

/** Unitless, such as emissivity, absorptivity and the lay ratio. */
export const formatConductorRatio = scaled(1, "", 2)

/**
 * [1/°C] in the ×10⁻⁶ form datasheets use. The suffix carries digits of its own,
 * which the shared formatter would space out, so it is appended here instead.
 */
export const formatConductorThermalExpansion = (value: number, locale: string) =>
  `${scaled(1e6, "", 1)(value, locale)} ×10⁻⁶/°C`

/** Most of a conductor's fields are optional, so an unset one has nothing to show. */
const formatOptional = <T>(
  value: T | null | undefined,
  format: (value: T) => string,
): string | undefined => (fp.isNil(value) ? undefined : format(value))

export const getConductorResistanceColumns = (
  t: TFunction,
  locale: string,
): SimpleTableColumn<ConductorResistanceSpec>[] => [
  {
    key: "current_type",
    labelKey: "conductors.CURRENT_TYPE",
    render: (spec) => conductorEnumLabel(t, "CURRENT_TYPES", spec.current_type),
  },
  {
    key: "at_temp",
    labelKey: "conductors.AT_TEMPERATURE",
    render: (spec) => formatConductorTemperature(spec.at_temp, locale),
  },
  {
    key: "ohm_per_m",
    labelKey: "conductors.RESISTANCE",
    render: (spec) => formatConductorResistance(spec.ohm_per_m, locale),
  },
]

export const getConductorLayerColumns = (
  t: TFunction,
  locale: string,
): SimpleTableColumn<ConductorLayer>[] => [
  {
    key: "layer_index",
    labelKey: "conductors.LAYER",
    render: (layer) =>
      layer.layer_index === 1
        ? t("conductors.LAYER_CORE", { index: layer.layer_index })
        : (layer.layer_index ?? "-"),
  },
  {
    key: "strands_count",
    labelKey: "conductors.STRANDS",
    render: (layer) => layer.strands_count ?? "-",
  },
  {
    key: "material",
    labelKey: "conductors.MATERIAL",
    render: (layer) =>
      conductorEnumLabel(t, "MATERIALS", layer.strand_properties?.material) ?? "-",
  },
  {
    key: "shape",
    labelKey: "conductors.SHAPE",
    render: (layer) =>
      conductorEnumLabel(t, "SHAPES", layer.strand_properties?.shape) ?? "-",
  },
  {
    key: "strand_size",
    labelKey: "conductors.STRAND_SIZE",
    render: (layer) =>
      formatOptional(getStrandDiameter(layer.strand_properties?.dimensions), (value) =>
        formatConductorLength(value, locale),
      ) ?? "-",
  },
  {
    key: "lay_ratio",
    labelKey: "conductors.LAY_RATIO",
    render: (layer) =>
      formatOptional(layer.lay_ratio, (value) => formatConductorRatio(value, locale)) ??
      "-",
  },
]

export interface SpecGroup {
  titleKey: string
  fields: SpecField[]
}

/**
 * What the conductor spec shows: its property groups, the catalog record and
 * whatever the API sends that the spec does not place.
 */
export const getConductorSpecGroups = (
  conductor: Conductor,
  locale: string,
): { groups: SpecGroup[]; record: SpecGroup | undefined; unknownFields: SpecField[] } => {
  const groups: SpecGroup[] = [
    {
      titleKey: "conductors.GEOMETRY",
      fields: [
        {
          labelKey: "conductors.DIAMETER",
          value: formatConductorLength(conductor.diameter, locale),
        },
        {
          labelKey: "conductors.INNER_DIAMETER",
          value: formatOptional(conductor.inner_diameter, (value) =>
            formatConductorLength(value, locale),
          ),
        },
        {
          labelKey: "conductors.NOMINAL_SECTION",
          value: formatOptional(conductor.nominal_section, (value) =>
            formatConductorSection(value, locale),
          ),
        },
        {
          labelKey: "conductors.MASS_PER_UNIT_LENGTH",
          value: formatConductorMass(conductor.mass_per_unit_length, locale),
        },
      ],
    },
    {
      titleKey: "conductors.MECHANICAL",
      fields: [
        {
          labelKey: "conductors.RATED_TENSILE_STRENGTH",
          value: formatOptional(conductor.rated_tensile_strength, (value) =>
            formatConductorForce(value, locale),
          ),
        },
        {
          labelKey: "conductors.ULTIMATE_TENSILE_STRENGTH",
          value: formatOptional(conductor.ultimate_tensile_strength, (value) =>
            formatConductorStress(value, locale),
          ),
        },
        {
          labelKey: "conductors.ENDURANCE_LIMIT",
          value: formatConductorStress(conductor.endurance_limit, locale),
        },
        {
          labelKey: "conductors.BENDING_AMPLITUDE_LIMIT",
          value: formatOptional(conductor.bending_amplitude_limit, (value) =>
            formatConductorLength(value, locale),
          ),
        },
      ],
    },
    {
      titleKey: "conductors.THERMAL_AND_RADIATIVE",
      fields: [
        {
          labelKey: "conductors.TEMPERATURE_MAX",
          value: formatOptional(conductor.temperature_max, (value) =>
            formatConductorTemperature(value, locale),
          ),
        },
        {
          labelKey: "conductors.EMERGENCY_TEMPERATURE_MAX",
          value: formatOptional(conductor.emergency_temperature_max, (value) =>
            formatConductorTemperature(value, locale),
          ),
        },
        {
          labelKey: "conductors.HEAT_CAPACITY",
          value: formatOptional(conductor.heat_capacity, (value) =>
            formatConductorHeatCapacity(value, locale),
          ),
        },
        {
          labelKey: "conductors.COEF_THERMAL_EXPANSION",
          value: formatOptional(conductor.coef_thermal_expansion, (value) =>
            formatConductorThermalExpansion(value, locale),
          ),
        },
        {
          labelKey: "conductors.EMISSIVITY",
          value: formatOptional(conductor.emissivity, (value) =>
            formatConductorRatio(value, locale),
          ),
        },
        {
          labelKey: "conductors.ABSORPTIVITY",
          value: formatOptional(conductor.absorptivity, (value) =>
            formatConductorRatio(value, locale),
          ),
        },
      ],
    },
    // A group the conductor has nothing for is dropped, rather than taking up
    // half a row to say so.
  ].filter(({ fields }) => fields.some(({ value }) => value !== undefined))

  // The catalog entry's provenance rather than part of the spec, so it rides
  // beside geometry instead of among the property groups.
  const recordFields = [
    {
      labelKey: "conductors.CREATED_AT",
      value: formatOptional(conductor.created_at, formatDayFromISO),
    },
    {
      labelKey: "conductors.UPDATED_AT",
      value: formatOptional(conductor.updated_at, formatDayFromISO),
    },
  ]
  const record = recordFields.some(({ value }) => value !== undefined)
    ? { titleKey: "conductors.RECORD", fields: recordFields }
    : undefined

  // Whatever the API grew that the spec has no wording or unit for. Shown bare
  // rather than dropped, so a new field is visible the day it arrives.
  const unknownFields = Object.entries(conductor)
    .filter(([name]) => !(name in PLACED_FIELDS))
    .map(([name, value]) => ({
      labelKey: humanizeApiName(name),
      value: formatUnknownValue(value, locale),
    }))
    .filter(({ value }) => value !== undefined)

  return { groups, record, unknownFields }
}
